import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import './CheckinAnalytics.css';

const RANGES = [{ d: 7, l: '7 days' }, { d: 30, l: '30 days' }, { d: 90, l: '90 days' }];
const shorten = (s) => (s && s.length > 18 ? s.slice(0, 17) + '…' : s);

// Key metric for a question, used in the by-class table
const metric = (q) => {
  if (q.qtype === 'scale' || q.qtype === 'faces') return q.avg != null ? q.avg.toFixed(2) : '—';
  if (q.qtype === 'yesno') return `${q.yes_rate}%`;
  return q.responses || 0;
};

function Sparkline({ points }) {
  if (!points || points.length === 0) return <span className="cka-muted">—</span>;
  return (
    <div className="cka-spark" title="Daily average">
      {points.map((p, i) => (
        <div key={i} className="cka-spark-bar" style={{ height: `${Math.max(6, (p.avg / 5) * 100)}%` }} title={`${p.date}: ${p.avg}`} />
      ))}
    </div>
  );
}

// Trends from the attendance check-in questions — gym-wide + per class.
function CheckinAnalytics({ days: propDays, classId = 'all', athleteId = 'all', level = 'all' } = {}) {
  const [internalDays, setInternalDays] = useState(30);
  const controlled = propDays != null;      // parent dashboard drives the window + filters
  const days = controlled ? propDays : internalDays;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API_BASE_URL}/api/checkin/analytics?days=${days}&athlete_id=${athleteId}&class_id=${classId}&level=${level}`);
      setData(r.data);
    } catch { setData(null); } finally { setLoading(false); }
  }, [days, athleteId, classId, level]);
  useEffect(() => { load(); }, [load]);

  const Header = () => (
    <div className="cka-head">
      <h2>📋 Check-in Trends</h2>
      {!controlled && (
        <div className="cka-range">
          {RANGES.map(r => (
            <button key={r.d} className={days === r.d ? 'on' : ''} onClick={() => setInternalDays(r.d)}>{r.l}</button>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) return <div className="cka"><Header /><div className="cka-loading">Loading check-in trends…</div></div>;

  if (!data || !data.questions?.length) {
    return (
      <div className="cka">
        <Header />
        <p className="cka-empty">
          No check-in questions yet. Add them under <strong>Operations → Classes &amp; Attendance → ⚙️ Check-in questions</strong>,
          and every answer athletes give at check-in will trend here — for the whole gym and by class.
        </p>
      </div>
    );
  }

  const anyResponses = data.gym.some(q => q.responses > 0);

  return (
    <div className="cka">
      <Header />
      {!anyResponses && (
        <p className="cka-empty">No responses in this window yet. As athletes check in and answer, their trends appear here.</p>
      )}

      {/* Gym-wide, per question */}
      <div className="cka-cards">
        {data.gym.map(q => (
          <div key={q.question_id} className="cka-card">
            <div className="cka-card-prompt">{q.prompt}</div>
            {(q.qtype === 'scale' || q.qtype === 'faces') && (
              <div className="cka-card-body">
                <div className="cka-card-val">{q.avg != null ? q.avg.toFixed(2) : '—'}<small> avg</small></div>
                <Sparkline points={q.trend} />
              </div>
            )}
            {q.qtype === 'yesno' && (
              <div className="cka-card-body">
                <div className={`cka-card-val ${q.yes_rate > 0 ? 'cka-warn' : ''}`}>{q.yes_rate}%<small> yes</small></div>
                <div className="cka-card-sub">{q.yes} yes · {q.no} no</div>
              </div>
            )}
            {q.qtype === 'text' && (
              <div className="cka-samples">
                {(q.samples || []).slice(-4).map((s, i) => <span key={i} className="cka-sample">{s}</span>)}
                {!q.samples?.length && <span className="cka-muted">no answers yet</span>}
              </div>
            )}
            <div className="cka-card-n">{q.responses} response{q.responses === 1 ? '' : 's'} · {q.athletes} athlete{q.athletes === 1 ? '' : 's'}</div>
          </div>
        ))}
      </div>

      {/* By class */}
      {data.byClass.length > 0 && (
        <div className="cka-byclass">
          <h3>By class</h3>
          <div className="cka-table-wrap">
            <table className="cka-table">
              <thead>
                <tr>
                  <th>Class</th>
                  {data.questions.map(q => <th key={q.id} title={q.prompt}>{shorten(q.prompt)}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr className="cka-gymrow">
                  <td>🏫 All (gym)</td>
                  {data.gym.map(q => <td key={q.question_id}>{q.responses ? metric(q) : <span className="cka-muted">—</span>}</td>)}
                </tr>
                {data.byClass.map(cl => (
                  <tr key={cl.class_id}>
                    <td>{cl.class_name}</td>
                    {cl.questions.map(q => <td key={q.question_id}>{q.responses ? metric(q) : <span className="cka-muted">—</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cka-legend">Scale = avg (1–5) · Yes/No = % answering yes · Text = # answers</p>
        </div>
      )}
    </div>
  );
}

export default CheckinAnalytics;
